import clsx from "clsx";
import orderBy from "lodash.orderby";
import { TriangleAlert } from "lucide-react";
import React, { useMemo } from "react";
import { useImmer } from "use-immer";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import { OrganizationListDto } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import { orgListFilter } from "@src/components/Navigation/OrgPicker/org-picker.util";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";

interface OrgListProps {
  value: OrganizationListDto | null;
  onChange: (value: OrganizationListDto | null) => void;
  label?: string;
}

const Formatter = (
  data: OrganizationListDto,
  themeColors: { warning: string }
) => {
  return (
    <Grid container columnSpacing={1} alignItems={"center"}>
      <Grid>
        <IgTypography>{data.name}</IgTypography>
      </Grid>
      {!data.isInstalled ? (
        <Grid>
          <TriangleAlert color={themeColors.warning} size={16} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export const OrgList = ({ onChange, value, label }: OrgListProps) => {
  const theme = useTheme();
  const [nameBy, setNameBy] = useImmer<"name" | "shopId">("name");
  const [order, setOrder] = useImmer<"name" | "date">("name");
  const orgList = useGetOrgList();

  const options: OrganizationListDto[] = useMemo(
    () =>
      orderBy(
        orgList.data?.orgs || [],
        order === "name" ? ["name"] : ["createdAtTs"],
        order === "name" ? ["asc"] : ["desc"]
      ).map((org: OrganizationListDto) => ({
        id: org.id,
        name:
          nameBy === "name"
            ? org.name
            : org.shopId.replace(".myshopify.com", ""),
        shopId: org.shopId,
        isInstalled: org.isInstalled,
        createdAtTs: org.createdAtTs,
        roles: org.roles,
      })),
    [orgList.data?.orgs, nameBy, order]
  );

  return (
    <Grid size="grow" sx={{ width: "100%" }}>
      <VirtualAutoComplete
        fullWidth={true}
        onChange={(e, value: OrganizationListDto) => onChange(value)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableClearable={true}
        slotProps={{
          paper: {
            sx: {
              "&.MuiPaper-root": {
                width: "275px !important",
              },
            },
          },
          popper: { sx: { zIndex: 1400 } },
        }}
        sx={{ zIndex: "1400 !important", mt: label ? 1 : undefined }}
        value={value || ""}
        options={options}
        label={label}
        getOptionLabel={(option: OrganizationListDto) => option.name || ""}
        formatter={(data) =>
          Formatter(data as OrganizationListDto, {
            warning: theme.igIconColors.warning,
          })
        }
        renderInput={(params) => (
          <IgTextField
            {...params}
            fullWidth={true}
            className={clsx("OrgPicker-orgName", "OrgPicker-textfield")}
            variant={"outlined"}
          />
        )}
        filterOptions={(options, state) =>
          orgListFilter(options, state.inputValue)
        }
      />
    </Grid>
  );
};
